import React, { useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { setAuthState } from '../../store/slices/authSlice';
import { graphqlRequest } from '../../service/graphql-client';
import { GQ_SEND_VERIFICATION_CODE, GQ_VERIFY_CODE, GQ_COMPLETE_REGISTRATION } from '../../service/graphql-mutations';

export default function SignupModal({ onClose, onSuccess, onShowLogin }) {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState(1); // 1: email, 2: verify code, 3: complete profile
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const data = await graphqlRequest(GQ_SEND_VERIFICATION_CODE, {
        email: formData.email
      });

      if (data.sendVerificationCode && data.sendVerificationCode.success) {
        setEmail(formData.email);
        setStep(2);
        setErrorMessage('');
      } else {
        setErrorMessage(data.sendVerificationCode?.message || 'Failed to send verification code');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      const data = await graphqlRequest(GQ_VERIFY_CODE, {
        email: email,
        code: formData.verificationCode
      });

      if (data.verifyCode && data.verifyCode.success) {
        setVerificationCode(formData.verificationCode);
        setStep(3);
        setErrorMessage('');
      } else {
        setErrorMessage(data.verifyCode?.message || 'Invalid verification code');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteSignup = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const data = await graphqlRequest(GQ_COMPLETE_REGISTRATION, {
        email: email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phone
      });

      if (data.completeRegistration && data.completeRegistration.success && data.completeRegistration.token) {
        const token = data.completeRegistration.token;
        // Update Redux store with authentication data
        dispatch(setAuthState({
          user: token.user,
          accessToken: token.accessToken,
          refreshToken: token.refreshToken
        }));

        // Store tokens in localStorage
        localStorage.setItem('access_token', token.accessToken);
        localStorage.setItem('refresh_token', token.refreshToken);
        localStorage.setItem('user', JSON.stringify(token.user));

        onSuccess();
      } else {
        if (data.completeRegistration?.message) {
          console.error('[Signup] Complete registration failed:', data.completeRegistration.message);
        }
        setErrorMessage('An issue occurred while completing your registration. Please try again or contact support.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Create Account</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <p>Enter your email address to get started.</p>
        <form onSubmit={handleSendCode}>
          <div>
            <label htmlFor="email">Email Address:</label> <br />
            <input
              className="input_100"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Code'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="backToLogin">
          <span>Already have an account?</span>&nbsp;<button type="button" onClick={onShowLogin} className="link-button">Log in</button>
        </div>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Verify Your Email</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <p>We've sent a verification code to {email}. Please enter it below.</p>
        <form onSubmit={handleVerifyCode}>
          <div>
            <label htmlFor="verificationCode">Verification Code:</label> <br />
            <input
              className="input_100"
              name="verificationCode"
              type="text"
              value={formData.verificationCode}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify Code'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="backToLogin">
          <button type="button" onClick={() => setStep(1)} className="link-button">Back to Email</button>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Complete Your Profile</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <form onSubmit={handleCompleteSignup}>
          <div>
            <label htmlFor="email">Email Address:</label> <br />
            <input
              className="input_100"
              name="email"
              type="email"
              value={email}
              readOnly
              required
            />
          </div>
          <div>
            <label htmlFor="firstName">First Name:</label> <br />
            <input
              className="input_100"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="lastName">Last Name:</label> <br />
            <input
              className="input_100"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="phone">Phone Number:</label> <br />
            <input
              className="input_100"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="password">Password:</label><br />
            <input
              className="input_100"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label htmlFor="confirmPassword">Confirm Password:</label><br />
            <input
              className="input_100"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <button
              type="submit"
              className="orange-button button saveBuildButton"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="backToLogin">
          <span>Already have an account?</span>&nbsp;<button type="button" onClick={onShowLogin} className="link-button">Log in</button>
        </div>
      </div>
    </>
  );

  switch (step) {
    case 1:
      return renderStep1();
    case 2:
      return renderStep2();
    case 3:
      return renderStep3();
    default:
      return renderStep1();
  }
}